import { lazy, Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { useAppSelector } from '@/store/hooks';
import { decryptData } from '@/lib/utils/encryption';
import { IPO_BETA_ENABLED } from '@/utils/ipoFeatureFlag';

// Lazy load components for better performance

const Dashboard = lazy(() => import('@/pages/dashboard'));
const Profile = lazy(() => import('@/pages/profile'));
const APProfile = lazy(() => import('@/pages/profile'));
const KYC = lazy(() => import('@/pages/KYC'));
const Compliance = lazy(() => import('@/pages/compliance'));
const Depository = lazy(() => import('@/pages/Depository'));
// # old ipo
const IPO = lazy(() => import('@/pages/IPO'));
const IPODetailsPage = lazy(() => import('@/pages/IPO/pages/IPODetailsPage'));
const IPOOnline = lazy(() => import('@/pages/IPO/online'));
const IPOPhysical = lazy(() => import('@/pages/IPO/physical'));
// # new ipo
const IPOBeta = lazy(() => import('@/pages/IPO-Beta'));
const IPOBetaDetailsPage = lazy(() => import('@/pages/IPO-Beta/pages/IPOBetaDetailsPage'));
const IPOBetaOnline = lazy(() => import('@/pages/IPO-Beta/online'));
const IPOBetaPhysical = lazy(() => import('@/pages/IPO-Beta/physical'));
const StockScanner = lazy(() => import('@/pages/stock-scanner'));
const StockScannerDetailsPage = lazy(
  () => import('@/pages/stock-scanner/pages/StockScannerDetailsPage'),
);
const MarketInsight = lazy(() => import('@/pages/market-insight'));
const News = lazy(() => import('@/pages/news'));
const MTF = lazy(() => import('@/pages/MTF/index'));
const LiveRisk = lazy(() => import('@/pages/liveRisk'));
// const StockVerification = lazy(() => import('@/pages/stock-verification'));
const EPledge = lazy(() => import('@/pages/e-pledge'));
const EMandate = lazy(() => import('@/pages/mandate'));
const Downloads = lazy(() => import('@/pages/downloadCenter'));
const HelpDesk = lazy(() => import('@/pages/HelpDesk-optimized'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const Settings = lazy(() => import('@/pages/settings'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const EquityOrderHistory = lazy(() => import('@/pages/wealth/equity/orderHistory'));

// Account pages
const AccountsGlobalSummary = lazy(() => import('@/pages/accounts/global-summary'));
const AccountsClientDrCr = lazy(() => import('@/pages/accounts/client-dr-cr'));
const AccountsAging = lazy(() => import('@/pages/accounts/aging'));
const AccountsLatePaymentCharges = lazy(() => import('@/pages/accounts/late-payment-charges'));
const AccountsPayout = lazy(() => import('@/pages/accounts/payout'));
const AccountsRiskCommon = lazy(() => import('@/pages/accounts/risk-common'));

// Report pages
const ReportsLedger = lazy(() => import('@/pages/reports/ledger/index'));
const ReportsHolding = lazy(() => import('@/pages/reports/holdings'));
const ReportsPosition = lazy(() => import('@/pages/reports/positions'));
const ReportsGlobal = lazy(() => import('@/pages/reports/global'));
const ReportsIncomeTax = lazy(() => import('@/pages/reports/income-tax'));
const ReportsContractBill = lazy(() => import('@/pages/reports/contract-bill'));

// Wealth pages
// const WealthDashboard = lazy(() => import('@/pages/wealth'));
const WealthMutualFund = lazy(() => import('@/pages/wealth/mutual-fund'));
const WealthMutualFundFactsheet = lazy(() => import('@/pages/wealth/mutual-fund/factsheet'));
const WealthEquity = lazy(() => import('@/pages/wealth/equity'));
const WealthPMS = lazy(() => import('@/pages/wealth/pms'));
const WealthPmsFactsheet = lazy(() => import('@/pages/wealth/pms/schemes/scheme-details'));

// Auth pages
const Login = lazy(() => import('@/pages/auth/login'));
const ForgotPassword = lazy(() => import('@/pages/auth/forgot-password'));

// Debug pages (Development only)
const FirebaseDebugPage = lazy(() => import('@/pages/debug/FirebaseDebugPage'));
const TableLoadingDemo = lazy(() => import('@/pages/demo/table-loading'));
const LoadingStatesDemo = lazy(() => import('@/pages/demo/loading-states'));
const ClassyShimmerDemo = lazy(() => import('@/pages/demo/classy-shimmer'));
const ShimmerSearchTest = lazy(() => import('@/pages/test/shimmer-search'));

// Error pages
const NotFound = lazy(() => import('@/pages/NotFound'));

// Private Routes
const PrivateRoute = lazy(() => import('@/components/PrivateRoute/PrivateRoute'));
const PublicRoute = lazy(() => import('@/components/PublicRoute/PublicRoute'));

// Wrapper component for Suspense with loading fallback
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense>{children}</Suspense>
);

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
};

// Route guard to prevent team users from accessing brokerage-summary
const BrokerageSummaryRouteGuard = () => {
  const { session } = useAppSelector((state) => state.user);

  const isTeam = useMemo(() => {
    const userType = session?.user?.user_type;
    if (userType) return userType.toLowerCase() === 'team';
    try {
      const raw = sessionStorage.getItem('userData');
      if (raw) {
        const decrypted = decryptData(raw, import.meta.env.VITE_APP_SALT as string);
        return (decrypted?.user?.user_type || '').toLowerCase() === 'team';
      }
    } catch {
      // ignore
    }
    return false;
  }, [session?.user?.user_type]);

  if (isTeam) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <SuspenseWrapper>
        <AccountsGlobalSummary />
      </SuspenseWrapper>
    </ErrorBoundary>
  );
};

// Accounts index redirect: team users should go to client-dr-cr, others to brokerage-summary
const AccountsIndexRedirect = () => {
  const { session } = useAppSelector((state) => state.user);

  const isTeam = useMemo(() => {
    const userType = session?.user?.user_type;
    if (userType) return userType.toLowerCase() === 'team';
    try {
      const raw = sessionStorage.getItem('userData');
      if (raw) {
        const decrypted = decryptData(raw, import.meta.env.VITE_APP_SALT as string);
        return (decrypted?.user?.user_type || '').toLowerCase() === 'team';
      }
    } catch {
      // ignore
    }
    return false;
  }, [session?.user?.user_type]);

  return (
    <Navigate to={isTeam ? '/accounts/client-dr-cr' : '/accounts/brokerage-summary'} replace />
  );
};

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <PublicRoute />,
    children: [
      {
        index: true,
        element: <Login />,
      },
    ],
  },
  {
    path: '/forgot-password',
    element: <PublicRoute />,
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <SuspenseWrapper>
              <ForgotPassword />
            </SuspenseWrapper>
          </ErrorBoundary>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <PrivateRoute />,
    children: [
      {
        // Pathless layout route to properly nest app layout
        element: <Layout />,
        children: [
          {
            // Default route after authentication
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Dashboard />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'profile',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Profile />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'profile',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <APProfile />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'kyc',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <KYC />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'compliance',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Compliance />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'accounts',
            children: [
              {
                index: true,
                element: <AccountsIndexRedirect />,
              },
              {
                path: 'brokerage-summary',
                element: <BrokerageSummaryRouteGuard />,
              },
              {
                path: 'client-dr-cr',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <AccountsClientDrCr />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'aging',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <AccountsAging />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'late-payment-charges',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <AccountsLatePaymentCharges />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              // {
              //   path: 'payout',
              //   element: (
              //     <ErrorBoundary FallbackComponent={ErrorFallback}>
              //       <SuspenseWrapper>
              //         <AccountsPayout />
              //       </SuspenseWrapper>
              //     </ErrorBoundary>
              //   ),
              // },
              {
                path: 'risk-common',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <AccountsRiskCommon />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
            ],
          },
          {
            path: 'reports',
            children: [
              {
                index: true,
                element: <Navigate to="/reports/ledger" replace />,
              },
              {
                path: 'ledger',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsLedger />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'holding',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsHolding />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'position',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsPosition />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'global',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsGlobal />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'income-tax',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsIncomeTax />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'contract-bill',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <ReportsContractBill />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
            ],
          },
          {
            path: 'depository',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Depository />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          // # new ipo
          // # old ipo
          {
            path: 'ipo',
            children: [
              {
                index: true,
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>{IPO_BETA_ENABLED ? <IPOBeta /> : <IPO />}</SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'online',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      {IPO_BETA_ENABLED ? <IPOBetaOnline /> : <IPOOnline />}
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'physical',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      {IPO_BETA_ENABLED ? <IPOBetaPhysical /> : <IPOPhysical />}
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: ':symbol',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      {IPO_BETA_ENABLED ? <IPOBetaDetailsPage /> : <IPODetailsPage />}
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
            ],
          },
          {
            path: 'stock-scanner',
            children: [
              {
                index: true,
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <StockScanner />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: ':symbol',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <StockScannerDetailsPage />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
            ],
          },
          {
            path: 'market-insight',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <MarketInsight />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'news',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <News />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'mtf',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <MTF />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'live-risk',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <LiveRisk />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },

          // {
          //   path: 'stock-verification',
          //   element: (
          //     <ErrorBoundary FallbackComponent={ErrorFallback}>
          //       <SuspenseWrapper>
          //         <StockVerification />
          //       </SuspenseWrapper>
          //     </ErrorBoundary>
          //   ),
          // },
          {
            path: 'pledge',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <EPledge />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'mandate',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <EMandate />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'wealth',
            children: [
              // {
              //   index: true,
              //   element: (
              //     <ErrorBoundary FallbackComponent={ErrorFallback}>
              //       <SuspenseWrapper>
              //         <WealthDashboard />
              //       </SuspenseWrapper>
              //     </ErrorBoundary>
              //   ),
              // },
              {
                path: 'mutual-fund',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <WealthMutualFund />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'mutual-fund/factsheet/:schemeCode',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <WealthMutualFundFactsheet />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'equity',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <WealthEquity />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'pms',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <WealthPMS />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'pms/scheme/:schemeId',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <WealthPmsFactsheet />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
              {
                path: 'equity/order-history/:clientCode',
                element: (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <SuspenseWrapper>
                      <EquityOrderHistory />
                    </SuspenseWrapper>
                  </ErrorBoundary>
                ),
              },
            ],
          },
          {
            path: 'downloads',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Downloads />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'help-desk',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <HelpDesk />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'faq',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <FAQ />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'setting',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <Settings />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'notifications',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <NotificationsPage />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'debug/firebase-notifications',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <FirebaseDebugPage />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'demo/table-loading',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <TableLoadingDemo />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'demo/loading-states',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <LoadingStatesDemo />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'demo/classy-shimmer',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <ClassyShimmerDemo />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
          {
            path: 'test/shimmer-search',
            element: (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SuspenseWrapper>
                  <ShimmerSearchTest />
                </SuspenseWrapper>
              </ErrorBoundary>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <SuspenseWrapper>
          <NotFound />
        </SuspenseWrapper>
      </ErrorBoundary>
    ),
  },
] as RouteObject[]);
